import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse, AxiosError, InternalAxiosRequestConfig } from 'axios'
import {
  MaintenanceItem,
  MaintenanceFilters,
  MaintenanceRequest,
  Partner,
  PaginationInfo,
  BackendCacheMetadata,
  MaintenanceRequestResponse,
  StatusTransitionPayloadDTO,
  ApprovalPayloadDTO,
  MaintenanceUpdateCreateDTO,
  ReminderResponseDTO,
  StalledReportResponseDTO,
  AssetRecurrenceProfileDTO,
  RecurringGenerationAttemptDTO,
  RecurrenceUnit
} from '../types/maintenance'
import { User, LoginCredentials, AuthResponse } from '../types/user'
import { Asset } from '../types/maintenance'
import { cacheDebugLogger } from '../utils/cacheDebugLogger'
import { expandStatusFilter } from '../utils/status'

export type MaintenanceCacheInvalidationReason =
  | 'create'
  | 'update'
  | 'complete'
  | 'assign'
  | 'approve'
  | 'reject'
  | 'triage'
  | 'send-quotations'
  | 'add-quotation'
  | 'approve-cfo'
  | 'cfo-confirm'
  | 'submit-to-cfo'
  | 'auto_recurrence_create'
  | 'manual_recurrence_override'
  | 'recurrence_profile_update'

export interface MaintenanceCacheInvalidationEvent {
  reason: MaintenanceCacheInvalidationReason
  requestId?: string
  storeId?: string
  timestamp: number
}

type MaintenanceCacheListener = (event: MaintenanceCacheInvalidationEvent) => void

const maintenanceCacheListeners = new Set<MaintenanceCacheListener>()
const PENDING_INVALIDATION_GLOBAL_KEY = '__global__'

const normalizeStoreIdForCache = (storeId?: string | null): string | undefined =>
  storeId && storeId.trim().length > 0 ? storeId.trim().toLowerCase() : undefined

const pendingInvalidations = new Map<string, MaintenanceCacheInvalidationEvent>()

const recordPendingInvalidation = (event: MaintenanceCacheInvalidationEvent) => {
  const key = normalizeStoreIdForCache(event.storeId) ?? PENDING_INVALIDATION_GLOBAL_KEY
  pendingInvalidations.set(key, event)

  cacheDebugLogger.debug('maintenanceCacheEvents.recordPendingInvalidation', 'Registrando invalidação pendente', {
    key,
    reason: event.reason,
    requestId: event.requestId,
    storeId: event.storeId
  })

  console.debug('[maintenanceCacheEvents] recordPendingInvalidation', {
    key,
    reason: event.reason,
    requestId: event.requestId,
    storeId: event.storeId,
    pendingKeys: Array.from(pendingInvalidations.keys())
  })
}

const dispatchMaintenanceCacheEvent = (event: MaintenanceCacheInvalidationEvent) => {
  maintenanceCacheListeners.forEach(listener => {
    try {
      listener(event)
    } catch (listenerError) {
      console.error('maintenanceCacheEvents listener failed', listenerError)
    }
  })
}

export const maintenanceCacheEvents = {
  subscribe(listener: MaintenanceCacheListener) {
    maintenanceCacheListeners.add(listener)
    return () => maintenanceCacheListeners.delete(listener)
  },
  emit(event: MaintenanceCacheInvalidationEvent) {
    console.debug('[maintenanceCacheEvents] emit', {
      reason: event.reason,
      requestId: event.requestId,
      storeId: event.storeId,
      timestamp: event.timestamp
    })

    cacheDebugLogger.debug('maintenanceCacheEvents.emit', 'Emissão de evento de invalidação', {
      reason: event.reason,
      requestId: event.requestId,
      storeId: event.storeId,
      timestamp: event.timestamp
    })
    dispatchMaintenanceCacheEvent(event)
    recordPendingInvalidation(event)
  },
  consumePending(storeId?: string) {
    const normalizedStoreId = normalizeStoreIdForCache(storeId)
    const events: MaintenanceCacheInvalidationEvent[] = []

    console.debug('[maintenanceCacheEvents] consumePending:start', {
      requestedStoreId: storeId,
      normalizedStoreId,
      pendingKeys: Array.from(pendingInvalidations.keys())
    })

    const globalEvent = pendingInvalidations.get(PENDING_INVALIDATION_GLOBAL_KEY)

    cacheDebugLogger.debug('maintenanceCacheEvents.consumePending', 'Consumindo invalidações pendentes', {
      requestedStoreId: storeId,
      normalizedStoreId,
      pendingKeys: Array.from(pendingInvalidations.keys())
    })
    if (globalEvent) {
      events.push(globalEvent)
      pendingInvalidations.delete(PENDING_INVALIDATION_GLOBAL_KEY)
      console.debug('[maintenanceCacheEvents] consumePending:consumedGlobal', {
        reason: globalEvent.reason,
        requestId: globalEvent.requestId,
        storeId: globalEvent.storeId
      })
    }

    if (normalizedStoreId) {
      const storeEvent = pendingInvalidations.get(normalizedStoreId)
      if (storeEvent) {
        events.push(storeEvent)
        pendingInvalidations.delete(normalizedStoreId)
        console.debug('[maintenanceCacheEvents] consumePending:consumedStore', {
          reason: storeEvent.reason,
          requestId: storeEvent.requestId,
          storeId: storeEvent.storeId
        })
      }
    } else if (events.length === 0) {
      // No store specified and nothing global consumed, flush all remaining events
      pendingInvalidations.forEach(event => {
        events.push(event)
        console.debug('[maintenanceCacheEvents] consumePending:consumedFallback', {
          reason: event.reason,
          requestId: event.requestId,
          storeId: event.storeId
        })
      })
      pendingInvalidations.clear()
    }

    console.debug('[maintenanceCacheEvents] consumePending:end', {
      requestedStoreId: storeId,
      normalizedStoreId,
      consumedCount: events.length,
      remainingKeys: Array.from(pendingInvalidations.keys())
    })

    cacheDebugLogger.debug('maintenanceCacheEvents.consumePending', 'Consumo concluído de invalidações pendentes', {
      requestedStoreId: storeId,
      normalizedStoreId,
      consumedCount: events.length,
      remainingKeys: Array.from(pendingInvalidations.keys())
    })

    return events
  }
}

const deriveStoreIdFromItem = (item?: MaintenanceItem | null): string | undefined => {
  if (!item) {
    return undefined
  }

  const requestLike = item as Partial<MaintenanceRequest>
  if (requestLike.asset?.store) {
    return requestLike.asset.store.trim().toLowerCase()
  }

  return undefined
}

const emitMaintenanceCacheInvalidation = (
  reason: MaintenanceCacheInvalidationReason,
  item?: MaintenanceItem | null,
  fallbackStoreId?: string,
  requestIdOverride?: string
) => {
  const normalizedFallbackStoreId = fallbackStoreId?.trim().toLowerCase()
  const derivedStoreId = deriveStoreIdFromItem(item)
  const eventPayload: MaintenanceCacheInvalidationEvent = {
    reason,
    requestId: requestIdOverride || item?.id,
    storeId: normalizedFallbackStoreId || derivedStoreId,
    timestamp: Date.now()
  }

  console.debug('[maintenanceCacheEvents] emitMaintenanceCacheInvalidation', {
    reason: eventPayload.reason,
    requestId: eventPayload.requestId,
    storeId: eventPayload.storeId
  })

  cacheDebugLogger.appendCacheInvalidation(
    'maintenanceCacheEvents.emitMaintenanceCacheInvalidation',
    eventPayload.reason,
    {
      requestId: eventPayload.requestId,
      storeId: eventPayload.storeId,
      extra: {
        fallbackStoreId: normalizedFallbackStoreId,
        derivedStoreId
      }
    }
  )

  maintenanceCacheEvents.emit(eventPayload)
}

/**
 * API error types
 */
export interface ApiError {
  success: false
  message: string
  code: string
  statusCode: number
  details?: Record<string, unknown>
  timestamp: string
  path: string
  method: string
}

/**
 * Concrete Error subclass for API failures. Rejecting with a real Error
 * (instead of a POJO) ensures Sentry captures a usable stack trace and
 * message — previously `Promise.reject({ code, error })` produced
 * "Object captured as promise rejection with keys: code, error" with
 * zero stack frames.
 */
export class ApiServiceError extends Error implements ApiError {
  readonly success = false as const
  readonly code: string
  readonly statusCode: number
  readonly details?: Record<string, unknown>
  readonly timestamp: string
  readonly path: string
  readonly method: string

  constructor(payload: ApiError) {
    super(payload.message || 'Erro inesperado na API')
    this.name = 'ApiServiceError'
    this.code = payload.code
    this.statusCode = payload.statusCode
    this.details = payload.details
    this.timestamp = payload.timestamp
    this.path = payload.path
    this.method = payload.method
  }
}

/**
 * API response wrapper
 */
export interface ApiResponse<T = unknown> {
  success: boolean
  data?: T
  message?: string
  code?: string
  errors?: unknown[]
  pagination?: PaginationInfo
}

/**
 * Extended request configuration for internal use
 */
interface ExtendedAxiosRequestConfig extends InternalAxiosRequestConfig {
  skipAuth?: boolean
  metadata?: {
    startTime: number
    requestId: string
  }
}

/**
 * API request configuration
 */
interface RequestConfig extends AxiosRequestConfig {
  skipAuth?: boolean
  skipErrorHandler?: boolean
}

import { preservePostLoginRedirect } from '../utils/postLoginRedirect'

/**
 * API Service class
 */
class ApiService {
  private client: AxiosInstance
  private static instance: ApiService
  private pendingRequests: Map<string, Promise<unknown>> = new Map()

  constructor() {
    // Safe fallback for import.meta.env to avoid SyntaxError in Jest/CJS
    let VITE_API_URL: string | undefined;
    try {
      const im = new Function('return import.meta')();
      VITE_API_URL = im?.env?.VITE_API_BASE_URL;
    } catch {
      // Silently ignore errors in test environment
    }
    // Use relative URL to work with Vite proxy
    const API_BASE_URL = (VITE_API_URL || '/api') as string;
    
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    })

    this.setupInterceptors()
  }

  /**
   * Get singleton instance
   */
  static getInstance(): ApiService {
    if (!ApiService.instance) {
      ApiService.instance = new ApiService()
    }
    return ApiService.instance
  }

  /**
   * Setup request and response interceptors
   */
  private setupInterceptors() {
    // Request interceptor - Add auth token and request ID
    this.client.interceptors.request.use(
      (config: ExtendedAxiosRequestConfig) => {
        const token = localStorage.getItem('authToken')
        if (token && !config.skipAuth) {
          config.headers.Authorization = `Bearer ${token}`
        }

        // Generate or reuse request ID
        let requestId = config.headers['X-Request-ID'] as string
        if (!requestId) {
          requestId = this.generateRequestId()
          config.headers['X-Request-ID'] = requestId
        }

        // Add request metadata
        config.metadata = {
          startTime: performance.now(),
          requestId
        }

        console.debug('API Request:', {
          method: config.method?.toUpperCase(),
          url: config.url,
          hasToken: !!token,
          tokenPreview: token ? `${token.substring(0, 20)}...` : 'none',
          authHeader: config.headers.Authorization && typeof config.headers.Authorization === 'string'
            ? `${config.headers.Authorization.substring(0, 30)}...`
            : 'none',
          requestId
        })

        return config
      },
      (error) => {
        console.error('Request interceptor error:', error)
        return Promise.reject(error)
      }
    )

    // Response interceptor - Handle responses and errors
    this.client.interceptors.response.use(
      (response) => {
        const config = response.config as ExtendedAxiosRequestConfig
        const duration = Math.round(performance.now() - (config?.metadata?.startTime || 0))
        const requestId = config?.metadata?.requestId
        
        console.debug('API Response:', {
          method: response.config.method?.toUpperCase(),
          url: response.config.url,
          status: response.status,
          durationMs: duration,
          requestId
        })

        // Log cache metadata if present
        if (response.data?.cacheMetadata) {
          const cacheMetadata = response.data.cacheMetadata
          console.debug('Cache Metadata:', {
            requestId,
            cacheStatus: cacheMetadata.cacheStatus,
            source: cacheMetadata.source,
            sheetsErrorCode: cacheMetadata.sheetsErrorCode,
            preservedDatasetAgeMs: cacheMetadata.preservedDatasetAgeMs,
            fallbackHitCount: cacheMetadata.fallbackHitCount
          })
        }

        return response
      },
      (error: AxiosError<ApiError>) => {
        const errorConfig = error.config as ExtendedAxiosRequestConfig
        const duration = Math.round(performance.now() - (errorConfig?.metadata?.startTime || 0))
        const requestId = errorConfig?.metadata?.requestId
        
        console.error('API Error:', {
          method: error.config?.method?.toUpperCase(),
          url: error.config?.url,
          status: error.response?.status,
          durationMs: duration,
          requestId,
          error: error.response?.data
        })

        // Handle 401 unauthorized - redirect to login
        if (error.response?.status === 401 && !errorConfig?.skipAuth) {
          console.warn('Authentication failed (401), clearing localStorage and redirecting to login')
          preservePostLoginRedirect()
          localStorage.removeItem('authToken')
          localStorage.removeItem('authUser')
          window.location.href = '/'
          return Promise.reject(error)
        }

        // Handle 403 forbidden
        if (error.response?.status === 403) {
          // Check if 403 is due to authentication issues (expired token, etc)
          const errorMessage = error.response?.data?.message || ''
          if (errorMessage.includes('Token') || errorMessage.includes('autenticação')) {
            console.warn('Authentication failed (403), clearing localStorage and redirecting to login')
            preservePostLoginRedirect()
            localStorage.removeItem('authToken')
            localStorage.removeItem('authUser')
            window.location.href = '/'
            return Promise.reject(error)
          }
          console.error('Access forbidden - insufficient permissions')
        }

        // Handle 429 rate limit
        if (error.response?.status === 429) {
          const retryAfter = error.response.headers['retry-after']
          console.warn(`Rate limited. Retry after: ${retryAfter} seconds`)
        }

        return Promise.reject(new ApiServiceError(this.transformError(error)))
      }
    )
  }

  /**
   * Generate unique request ID
   */
  private generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * Generate request key for deduplication
   */
  private generateRequestKey(method: string, url: string, params?: Record<string, unknown>): string {
    const paramString = params ? JSON.stringify(params) : ''
    return `${method}:${url}:${paramString}`
  }

  /**
   * Check if request is already pending and return existing promise
   */
  private getPendingRequest<T>(key: string): Promise<T> | null {
    return this.pendingRequests.get(key) as Promise<T> || null
  }

  /**
   * Store pending request and add cleanup
   */
  private setPendingRequest<T>(key: string, promise: Promise<T>): Promise<T> {
    this.pendingRequests.set(key, promise)
    
    // Clean up after request completes (success or error)
    promise.finally(() => {
      this.pendingRequests.delete(key)
    })
    
    return promise
  }

  /**
   * Transform axios error to consistent format
   */
  private transformError(error: AxiosError<ApiError>): ApiError {
    if (error.response?.data) {
      // Enhanced error handling for maintenance endpoints
      const statusCode = error.response.status
      const path = error.config?.url || ''
      
      // Specific error messages for maintenance workflow
      if (path.includes('/maintenance/')) {
        if (path.includes('/status')) {
          if (statusCode === 400) {
            return {
              ...error.response.data,
              message: 'Transição de status inválida. Verifique se os requisitos foram atendidos.',
              code: 'STATUS_TRANSITION_ERROR'
            }
          }
          if (statusCode === 403) {
            return {
              ...error.response.data,
              message: 'Você não tem permissão para realizar esta alteração de status.',
              code: 'STATUS_PERMISSION_ERROR'
            }
          }
        }
        
        if (path.includes('/approve')) {
          if (statusCode === 403) {
            return {
              ...error.response.data,
              message: 'Você não tem permissão para aprovar esta requisição ou o custo excede seu limite.',
              code: 'APPROVAL_PERMISSION_ERROR'
            }
          }
          if (statusCode === 409) {
            return {
              ...error.response.data,
              message: 'Esta requisição já foi aprovada ou rejeitada.',
              code: 'APPROVAL_CONFLICT_ERROR'
            }
          }
        }
        
        if (path.includes('/updates')) {
          if (statusCode === 400) {
            return {
              ...error.response.data,
              message: 'A mensagem deve ter entre 10 e 500 caracteres.',
              code: 'UPDATE_VALIDATION_ERROR'
            }
          }
        }
        
        if (path.includes('/remind')) {
          if (statusCode === 429) {
            return {
              ...error.response.data,
              message: 'Um lembrete já foi enviado recentemente. Aguarde antes de enviar outro.',
              code: 'REMINDER_RATE_LIMIT_ERROR'
            }
          }
        }

        // Falha de servidor ao criar a requisição (guard de layout da planilha,
        // Sheets indisponível, etc.). O backend devolve code 'CREATE_REQUEST_ERROR'
        // com mensagem técnica em inglês — substituímos por uma mensagem amigável
        // em PT. O detalhe técnico permanece registado nos logs do backend.
        if ((error.response.data as Partial<ApiError>)?.code === 'CREATE_REQUEST_ERROR') {
          return {
            ...error.response.data,
            message: 'Não foi possível guardar a requisição na planilha (possível problema de formatação). A requisição NÃO foi criada — tente novamente e, se o problema persistir, contacte o suporte.',
            code: 'CREATE_REQUEST_ERROR'
          }
        }
      }

      // Reports endpoint errors
      if (path.includes('/reports/')) {
        if (statusCode === 403) {
          return {
            ...error.response.data,
            message: 'Você não tem permissão para acessar relatórios.',
            code: 'REPORT_PERMISSION_ERROR'
          }
        }
      }

      // Generic fallback: backend may return arbitrary shapes like
      // `{ code, error }`. Build a complete ApiError so the resulting
      // ApiServiceError always carries a usable message and status.
      const raw = (error.response.data ?? {}) as Partial<ApiError> & { error?: unknown }
      const rawErrorText = typeof raw.error === 'string' ? raw.error : undefined
      const fallbackMessage =
        raw.message ||
        rawErrorText ||
        (statusCode === 403 ? 'Você não tem permissão para executar esta ação.'
          : statusCode === 404 ? 'Recurso não encontrado.'
          : statusCode >= 500 ? 'Erro no servidor. Tente novamente em instantes.'
          : 'Falha ao comunicar com o servidor.')
      return {
        success: false,
        message: fallbackMessage,
        code: raw.code || `HTTP_${statusCode}`,
        statusCode,
        details: raw.details ?? (rawErrorText ? undefined : raw),
        timestamp: raw.timestamp || new Date().toISOString(),
        path,
        method: error.config?.method?.toUpperCase() || 'UNKNOWN'
      }
    }

    // Network or timeout errors
    if (error.code === 'ECONNABORTED') {
      return {
        success: false,
        message: 'A requisição demorou muito tempo. Tente novamente.',
        code: 'TIMEOUT',
        statusCode: 408,
        timestamp: new Date().toISOString(),
        path: error.config?.url || '',
        method: error.config?.method?.toUpperCase() || 'UNKNOWN'
      }
    }

    if (!error.response) {
      return {
        success: false,
        message: 'Erro de conexão. Verifique sua internet e tente novamente.',
        code: 'NETWORK_ERROR',
        statusCode: 0,
        timestamp: new Date().toISOString(),
        path: error.config?.url || '',
        method: error.config?.method?.toUpperCase() || 'UNKNOWN'
      }
    }

    return {
      success: false,
      message: 'Ocorreu um erro inesperado. Tente novamente.',
      code: 'UNKNOWN_ERROR',
      statusCode: error.response.status,
      timestamp: new Date().toISOString(),
      path: error.config?.url || '',
      method: error.config?.method?.toUpperCase() || 'UNKNOWN'
    }
  }

  /**
   * Generic request method
   */
  private async request<T = unknown>(config: RequestConfig): Promise<ApiResponse<T>> {
    try {
      const response: AxiosResponse<ApiResponse<T>> = await this.client.request(config)
      
      if (response.data.success === false) {
        throw new Error(response.data.message || 'Request failed')
      }

      return response.data
    } catch (error) {
      if (config.skipErrorHandler) {
        throw error
      }
      throw error
    }
  }

  /**
   * Authentication methods
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>({
      method: 'POST',
      url: '/auth/login',
      data: credentials
    })

    return response.data!
  }

  async logout(): Promise<void> {
    try {
      await this.request({
        method: 'POST',
        url: '/auth/logout'
      })
    } catch {
      // Even if logout fails, clear local storage
      console.warn('Logout request failed, but clearing local storage')
    } finally {
      localStorage.removeItem('authToken')
      localStorage.removeItem('authUser')
    }
  }

  /**
   * Asset methods
   */
  async searchAssets(params?: {
    store?: string | string[]
    department?: string | string[]
    search?: string
    approvalLevel?: string
    assetType?: string
    page?: number
    limit?: number
    populate?: string
  }): Promise<Asset[]> {
    // Adiciona populate=partner por padrão se não especificado
    const searchParams = { ...params }
    if (!searchParams.populate) {
      searchParams.populate = 'partner'
    }
    
    // Faz a requisição diretamente com o cliente axios para obter a resposta bruta
    const response = await this.client.get<{assets: Asset[], total: number}>('/assets/search', { params: searchParams })
    
    // O backend retorna {assets: Array(78), total: 78} diretamente
    // Garante que campos de recorrência sejam incluídos mesmo que undefined
    const assets = response.data.assets || []
    return assets.map(asset => ({
      ...asset,
      // Campos de recorrência com tratamento de undefined para consistência
      recurrenceQuantity: asset.recurrenceQuantity ?? undefined,
      recurrenceUnit: asset.recurrenceUnit ?? undefined,
      recurrenceString: asset.recurrenceString ?? undefined,
      recurrenceNextDueDate: asset.recurrenceNextDueDate ?? undefined,
      recurrenceLastExecution: asset.recurrenceLastExecution ?? undefined,
      recurrenceInactive: asset.recurrenceInactive ?? false,
      recurrenceSource: asset.recurrenceSource ?? undefined,
      recurrenceNotes: asset.recurrenceNotes ?? undefined,
      recurrenceStatusHistory: asset.recurrenceStatusHistory ?? [],
      recurrenceErrorCount: asset.recurrenceErrorCount ?? 0,
      recurrenceLastError: asset.recurrenceLastError ?? undefined,
      recurrenceLastAttemptAt: asset.recurrenceLastAttemptAt ?? undefined
    }))
  }

  async getAsset(id: string, populate?: string): Promise<Asset> {
    const params = populate ? { populate } : { populate: 'partner' }
    const response = await this.request<Asset>({
      method: 'GET',
      url: `/assets/${id}`,
      params
    })

    const asset = response.data!
    // Garante que campos de recorrência sejam incluídos mesmo que undefined
    return {
      ...asset,
      recurrenceQuantity: asset.recurrenceQuantity ?? undefined,
      recurrenceUnit: asset.recurrenceUnit ?? undefined,
      recurrenceString: asset.recurrenceString ?? undefined,
      recurrenceNextDueDate: asset.recurrenceNextDueDate ?? undefined,
      recurrenceLastExecution: asset.recurrenceLastExecution ?? undefined,
      recurrenceInactive: asset.recurrenceInactive ?? false,
      recurrenceSource: asset.recurrenceSource ?? undefined,
      recurrenceNotes: asset.recurrenceNotes ?? undefined,
      recurrenceStatusHistory: asset.recurrenceStatusHistory ?? [],
      recurrenceErrorCount: asset.recurrenceErrorCount ?? 0,
      recurrenceLastError: asset.recurrenceLastError ?? undefined,
      recurrenceLastAttemptAt: asset.recurrenceLastAttemptAt ?? undefined
    }
  }

  /**
   * Maintenance request methods
   */
  async getMaintenanceRequests(params?: {
    status?: string[]
    urgency?: string[]
    department?: string[]
    /** Filtro rápido por localização/loja (chip da barra de controle). */
    storeIds?: string[]
    search?: string
    startDate?: string
    endDate?: string
    page?: number
    limit?: number
  }): Promise<{
    items?: MaintenanceItem[];
    requests?: MaintenanceItem[];
    pagination: PaginationInfo;
    cacheMetadata?: BackendCacheMetadata;
    requestId?: string;
    durationMs?: number;
  }> {
    const requestKey = this.generateRequestKey('GET', '/maintenance/requests', params)
    
    // Check if request is already pending
    const pendingRequest = this.getPendingRequest<{
      items?: MaintenanceItem[];
      requests?: MaintenanceItem[];
      pagination: PaginationInfo;
      cacheMetadata?: BackendCacheMetadata;
      requestId?: string;
      durationMs?: number;
    }>(requestKey)
    
    if (pendingRequest) {
      console.debug('Returning pending request for getMaintenanceRequests:', { requestKey })
      return pendingRequest
    }

    const requestConfig = {
      params,
      headers: {
        'Cache-Control': 'no-cache',
        Pragma: 'no-cache'
      }
    } as const

    const requestPromise = this.client
      .get<{
        requests: MaintenanceItem[];
        pagination: PaginationInfo;
        cacheMetadata?: BackendCacheMetadata;
      }>('/maintenance/requests', requestConfig)
      .then(response => {
      const data = response.data
      if (!data) {
        console.error('Response is undefined!')
        return {
          items: [],
          pagination: { page: 1, limit: 20, total: 0, totalPages: 0 }
        }
      }
      
      // Garante que campos de recorrência sejam incluídos nas requisições
      const requests = (data.requests || []).map(request => {
        const enrichedRequest = { ...request }
        
        // Garante que campos de recorrência do asset sejam incluídos
        if (enrichedRequest.asset) {
          enrichedRequest.asset = {
            ...enrichedRequest.asset,
            recurrenceQuantity: enrichedRequest.asset.recurrenceQuantity ?? undefined,
            recurrenceUnit: enrichedRequest.asset.recurrenceUnit ?? undefined,
            recurrenceString: enrichedRequest.asset.recurrenceString ?? undefined,
            recurrenceNextDueDate: enrichedRequest.asset.recurrenceNextDueDate ?? undefined,
            recurrenceLastExecution: enrichedRequest.asset.recurrenceLastExecution ?? undefined,
            recurrenceInactive: enrichedRequest.asset.recurrenceInactive ?? false,
            recurrenceSource: enrichedRequest.asset.recurrenceSource ?? undefined,
            recurrenceNotes: enrichedRequest.asset.recurrenceNotes ?? undefined,
            recurrenceStatusHistory: enrichedRequest.asset.recurrenceStatusHistory ?? [],
            recurrenceErrorCount: enrichedRequest.asset.recurrenceErrorCount ?? 0,
            recurrenceLastError: enrichedRequest.asset.recurrenceLastError ?? undefined,
            recurrenceLastAttemptAt: enrichedRequest.asset.recurrenceLastAttemptAt ?? undefined
          }
        }
        
        // Garante que recurrenceMetadata seja incluído na requisição
        enrichedRequest.recurrenceMetadata = enrichedRequest.recurrenceMetadata ?? undefined
        
        return enrichedRequest
      })
      
      return {
        items: requests,
        requests: requests,
        pagination: data.pagination || { page: 1, limit: 20, total: 0, totalPages: 0 },
        cacheMetadata: data.cacheMetadata
      }
    })

    // Store pending request
    return this.setPendingRequest(requestKey, requestPromise)
  }


  async downloadMaintenanceReport(
    format: 'xlsx' | 'pdf',
    filters: MaintenanceFilters
  ): Promise<{ blob: Blob; filename: string }> {
    const response = await this.client.get<Blob>('/maintenance/requests/export', {
      params: this.serializeMaintenanceFilters({ ...filters, format }),
      responseType: 'blob',
      timeout: 120000
    })
    const disposition = response.headers['content-disposition'] as string | undefined
    const filenameMatch = disposition?.match(/filename="?([^";]+)"?/i)
    return {
      blob: response.data,
      filename: filenameMatch?.[1] || `relatorio-requisicoes.${format}`
    }
  }

  async emailMaintenanceReport(payload: {
    format: 'xlsx' | 'pdf'
    recipients: string[]
    observations?: string
    filters: MaintenanceFilters
  }): Promise<{ success: boolean; count: number }> {
    const response = await this.client.post<{ success: boolean; count: number }>(
      '/maintenance/requests/export/email',
      {
        ...payload,
        filters: this.serializeMaintenanceFilters(payload.filters)
      },
      { timeout: 120000 }
    )
    return response.data
  }

  private serializeMaintenanceFilters(filters: MaintenanceFilters & { format?: 'xlsx' | 'pdf' }) {
    const expanded = expandStatusFilter({
      status: filters.status,
      subcategory: filters.subcategory,
    })
    return {
      status: expanded.status?.join(','),
      urgency: filters.urgency?.join(','),
      subcategory: expanded.subcategory?.join(','),
      department: filters.department?.join(','),
      responsibleId: filters.responsibleId?.join(','),
      storeIds: filters.storeIds?.join(','),
      startDate: filters.dateRange?.start?.toISOString(),
      endDate: filters.dateRange?.end?.toISOString(),
      search: filters.search,
      sortBy: filters.sortBy,
      sortOrder: filters.sortOrder,
      // NOTE(#248): este campo já existia antes do fix; o issue body foi corrigido para refletir isso.
      awaitingQuotationApproval: filters.awaitingQuotationApproval,
      format: filters.format
    }
  }

  async getMaintenanceRequest(id: string): Promise<MaintenanceRequestResponse> {
    const response = await this.request<MaintenanceRequestResponse>({
      method: 'GET',
      url: `/maintenance/${id}`
    })

    // CORREÇÃO: A API retorna dados diretamente, não em wrapper ApiResponse
    // Se response.data existe, retorna data, senão retorna response (compatibilidade)
    const requestData = response.data || (response as unknown as MaintenanceRequestResponse)
    
    // Garante que campos de recorrência sejam incluídos mesmo que undefined
    // MaintenanceRequestResponse contém diretamente os campos da requisição
    if (requestData.asset) {
      requestData.asset.recurrenceQuantity = requestData.asset.recurrenceQuantity ?? undefined
      requestData.asset.recurrenceUnit = requestData.asset.recurrenceUnit ?? undefined
      requestData.asset.recurrenceString = requestData.asset.recurrenceString ?? undefined
      requestData.asset.recurrenceNextDueDate = requestData.asset.recurrenceNextDueDate ?? undefined
      requestData.asset.recurrenceLastExecution = requestData.asset.recurrenceLastExecution ?? undefined
      requestData.asset.recurrenceInactive = requestData.asset.recurrenceInactive ?? false
      requestData.asset.recurrenceSource = requestData.asset.recurrenceSource ?? undefined
      requestData.asset.recurrenceNotes = requestData.asset.recurrenceNotes ?? undefined
      requestData.asset.recurrenceStatusHistory = requestData.asset.recurrenceStatusHistory ?? []
      requestData.asset.recurrenceErrorCount = requestData.asset.recurrenceErrorCount ?? 0
      requestData.asset.recurrenceLastError = requestData.asset.recurrenceLastError ?? undefined
      requestData.asset.recurrenceLastAttemptAt = requestData.asset.recurrenceLastAttemptAt ?? undefined
    }
    
    // Garante que recurrenceMetadata seja incluído na requisição
    if (requestData.recurrenceMetadata === undefined) {
      requestData.recurrenceMetadata = requestData.recurrenceMetadata ?? undefined
    }
    
    return requestData
  }

  /**
   * Atualiza o status de uma requisição de manutenção
   * Endpoint: PATCH /api/maintenance/{id}/status
   */
  async updateMaintenanceStatus(id: string, payload: StatusTransitionPayloadDTO): Promise<MaintenanceRequestResponse> {
    const response = await this.request<MaintenanceRequestResponse>({
      method: 'PATCH',
      url: `/maintenance/${id}/status`,
      data: payload
    })

    const updatedRequest = response.data!
    const invalidationReason = payload.targetStatus === 'APROVADO' ? 'approve' :
                              payload.targetStatus === 'REJEITADO' ? 'reject' :
                              payload.targetStatus === 'CONCLUIDO' ? 'complete' : 'update'
    
    emitMaintenanceCacheInvalidation(invalidationReason, updatedRequest as unknown as MaintenanceItem)
    return updatedRequest
  }

  /**
   * Adiciona um update a uma requisição de manutenção
   * Endpoint: POST /api/maintenance/{id}/updates
   */
  async postMaintenanceUpdate(id: string, payload: MaintenanceUpdateCreateDTO): Promise<MaintenanceRequestResponse> {
    const response = await this.request<{ success: boolean; update: MaintenanceRequestResponse; request: MaintenanceRequestResponse; message: string }>({
      method: 'POST',
      url: `/maintenance/${id}/updates`,
      data: payload
    })

    // O response pode ter os dados diretamente ou em response.data
    // Backend retorna { success, update, request, message }
    const responseWrapper = response.data;
    if (!responseWrapper) {
      throw new Error('API retornou resposta vazia');
    }

    const updatedRequest: MaintenanceRequestResponse = responseWrapper.request || (responseWrapper as unknown as MaintenanceRequestResponse);

    if (!updatedRequest || !updatedRequest.id) {
      throw new Error('API não retornou objeto request válido');
    }

    emitMaintenanceCacheInvalidation('update', updatedRequest as unknown as MaintenanceItem)
    return updatedRequest
  }

  /**
   * Aprova uma requisição de manutenção
   * Endpoint: POST /api/maintenance/{id}/approve
   */
  async approveMaintenanceRequest(id: string, payload?: ApprovalPayloadDTO): Promise<MaintenanceRequestResponse> {
    const response = await this.request<MaintenanceRequestResponse>({
      method: 'POST',
      url: `/maintenance/${id}/approve`,
      data: payload
    })

    // CORREÇÃO: A API retorna dados diretamente, não em wrapper ApiResponse
    // Se response.data existe, retorna data, senão retorna response (compatibilidade)
    const approvedRequest = response.data || response as unknown as MaintenanceRequestResponse
    
    if (!approvedRequest?.id) {
      console.error('approveMaintenanceRequest: Resposta inválida da API', { response, approvedRequest })
      throw new Error('Resposta inválida da API de aprovação')
    }
    
    emitMaintenanceCacheInvalidation('approve', approvedRequest as unknown as MaintenanceItem)
    return approvedRequest
  }

  /**
   * Envia um lembrete para uma requisição de manutenção
   * Endpoint: POST /api/maintenance/{id}/remind
   */
  async remindMaintenanceRequest(id: string, payload?: { force?: boolean }): Promise<ReminderResponseDTO> {
    const response = await this.request<ReminderResponseDTO>({
      method: 'POST',
      url: `/maintenance/${id}/remind`,
      data: payload
    })

    return response.data!
  }

  /**
   * Obtém relatório de requisições estagnadas
   * Endpoint: GET /api/reports/inventory-stalled
   */
  async getInventoryStalledReport(filters?: {
    status?: string
    days?: number
    storeId?: string
  }): Promise<StalledReportResponseDTO> {
    const response = await this.request<StalledReportResponseDTO>({
      method: 'GET',
      url: '/reports/inventory-stalled',
      params: filters
    })

    return response.data!
  }

  async createMaintenanceRequest(data: {
    equipmentId: string
    problemDescription: string
    urgency: string
    estimatedCost?: number
    equipmentDescription?: string
  }): Promise<MaintenanceItem> {
    // Backend retorna diretamente o objeto, não dentro de response.data
    const response = await this.client.post<MaintenanceItem>('/maintenance/requests', data)
    const createdRequest = response.data
    emitMaintenanceCacheInvalidation('create', createdRequest)
    return createdRequest
  }

  async updateMaintenanceRequest(id: string, data: {
    equipmentId?: string
    problemDescription?: string
    urgency?: string
    estimatedCost?: number
    attachmentUrls?: string[]  // ADICIONAR
  }): Promise<MaintenanceItem> {
    // Backend retorna dentro de response.data.data para updates
    const response = await this.client.put<{success: boolean, data: MaintenanceItem}>(`/maintenance/requests/${id}`, data)
    const updatedRequest = response.data.data
    emitMaintenanceCacheInvalidation('update', updatedRequest)
    return updatedRequest
  }

  /**
   * @deprecated Usar updateMaintenanceStatus para transições de estado
   * Mantido para compatibilidade com código existente
   */
  async approveMaintenanceRequestLegacy(id: string, data?: {
    approvalComments?: string
    supplierContacted?: string
  }): Promise<MaintenanceItem> {
    const response = await this.request<MaintenanceItem>({
      method: 'POST',
      url: `/maintenance/requests/${id}/approve`,
      data
    })

    const approvedRequest = response.data!
    emitMaintenanceCacheInvalidation('approve', approvedRequest)
    return approvedRequest
  }

  async rejectMaintenanceRequest(id: string, data: {
    reason: string
  }): Promise<MaintenanceItem> {
    const response = await this.client.request({
      method: 'POST',
      url: `/maintenance/requests/${id}/reject`,
      data
    })
  
    const rejectedRequest = response.data
    emitMaintenanceCacheInvalidation('reject', rejectedRequest)
    return rejectedRequest
  }

  async completeMaintenanceRequest(id: string, data: {
    executionDate: string
    actualCost: number
    solutionDescription: string
    responsibleTechnician: string
    rating?: number
    finalObservations?: string
    attachmentUrls?: string[]  // ADICIONAR
  }): Promise<MaintenanceItem> {
    const response = await this.request<MaintenanceItem>({
      method: 'POST',
      url: `/maintenance/requests/${id}/complete`,
      data
    })

    const completedRequest = response.data!
    emitMaintenanceCacheInvalidation('complete', completedRequest)
    return completedRequest
  }

  async assignMaintenanceResponsibility(requestId: string, newResponsibleId: string, comments?: string): Promise<unknown> {
    const response = await this.request({
      method: 'POST',
      url: `/maintenance/requests/${requestId}/assign`,
      data: { newResponsibleId, ...(comments !== undefined ? { comments } : {}) }
    })

    const assignmentResult = response.data!
    const fallbackStoreId = (assignmentResult as { storeId?: string | null })?.storeId
    const item = (assignmentResult && typeof assignmentResult === 'object'
      ? (assignmentResult as unknown as MaintenanceItem)
      : null)
    emitMaintenanceCacheInvalidation('assign', item, fallbackStoreId ?? undefined, requestId)
    return assignmentResult
  }

  /**
   * Realiza triagem de uma requisição de manutenção
   * POST /api/triage/:requestId
   */
  async performTriage(requestId: string, payload: {
    decision: 'NADA' | 'RESOLVE' | 'EU_RESOLVO' | 'CONVERTER_EM_COMPRA'
    notes?: string
  }): Promise<MaintenanceItem> {
    const response = await this.request<MaintenanceItem>({
      method: 'POST',
      url: `/triage/${requestId}`,
      data: payload
    })

    const updatedRequest = response.data!
    emitMaintenanceCacheInvalidation('triage', updatedRequest)
    return updatedRequest
  }

  /**
   * Envia emails de cotação para parceiros
   * POST /api/quotations/:requestId/send-emails
   */
  async sendQuotationEmails(requestId: string): Promise<MaintenanceItem> {
    const response = await this.request<MaintenanceItem>({
      method: 'POST',
      url: `/quotations/${requestId}/send-emails`
    })

    const updatedRequest = response.data!
    emitMaintenanceCacheInvalidation('send-quotations', updatedRequest)
    return updatedRequest
  }

  /**
   * Adiciona uma cotação à requisição
   * POST /api/quotations/:requestId
   */
  async addQuotation(requestId: string, payload: {
    partnerName: string
    contactEmail?: string
    contactPhone?: string
    quotedValue: number
    deliveryTime?: string
    notes?: string
  }): Promise<MaintenanceItem> {
    const response = await this.request<MaintenanceItem>({
      method: 'POST',
      url: `/quotations/${requestId}`,
      data: payload
    })

    const updatedRequest = response.data!
    emitMaintenanceCacheInvalidation('add-quotation', updatedRequest)
    return updatedRequest
  }

  /**
   * CFO aprova cotação selecionada
   * POST /api/quotations/:requestId/approve
   */
  async approveCFOQuotation(requestId: string, payload: {
    quotationId: string
    notes?: string
  }): Promise<MaintenanceItem> {
    const response = await this.request<MaintenanceItem>({
      method: 'POST',
      url: `/quotations/${requestId}/approve`,
      data: payload
    })

    const updatedRequest = response.data!
    emitMaintenanceCacheInvalidation('approve-cfo', updatedRequest)
    return updatedRequest
  }

  /**
   * CFO confirma valor final e conclui requisição
   * POST /api/maintenance/requests/:id/cfo-confirm
   */
  async confirmCFOValue(requestId: string, payload: {
    finalValue: number
    notes?: string
  }): Promise<MaintenanceItem> {
    const response = await this.request<MaintenanceItem>({
      method: 'POST',
      url: `/maintenance/requests/${requestId}/cfo-confirm`,
      data: payload
    })

    const updatedRequest = response.data!
    emitMaintenanceCacheInvalidation('cfo-confirm', updatedRequest)
    return updatedRequest
  }

  /**
   * Submete a requisição de compra para aprovação do CFO (issue #55).
   * Dispara o email `sendQuotationsFulfilledNotification` e regista
   * cfoNotifiedAt/cfoNotifiedBy/cfoNotifiedQuotationsCount no request.
   * POST /api/maintenance/requests/:id/submit-to-cfo
   */
  async submitForCfoApproval(requestId: string): Promise<MaintenanceItem> {
    const response = await this.request<MaintenanceItem>({
      method: 'POST',
      url: `/maintenance/requests/${requestId}/submit-to-cfo`
    })

    const updatedRequest = response.data!
    emitMaintenanceCacheInvalidation('submit-to-cfo', updatedRequest)
    return updatedRequest
  }

  /**
   * @deprecated Usar postMaintenanceUpdate com payload tipado
   * Mantido para compatibilidade com código existente
   */
  async postMaintenanceUpdateLegacy(requestId: string, data: {
    message: string
  }): Promise<MaintenanceItem> {
    const response = await this.request<MaintenanceItem>({
      method: 'POST',
      url: `/maintenance/requests/${requestId}/updates`,
      data
    })

    const updatedRequest = response.data!
    emitMaintenanceCacheInvalidation('update', updatedRequest)
    return updatedRequest
  }

  async getResponsibleUsers(params?: {
    storeId?: string
    departmentId?: string
    requestId?: string
  }): Promise<{
    id: string
    name: string
    email: string
    type: string
    store: string
    active: boolean
  }[]> {
    const response = await this.request<{
      success: boolean
      data: {
        id: string
        name: string
        email: string
        type: string
        store: string
        active: boolean
      }[]
      count: number
    }>({
      method: 'GET',
      url: '/maintenance/users/responsibles',
      params: params && Object.keys(params).length > 0 ? params : undefined
    })

    // request() já devolve o body desembrulhado, por isso `response.data` É o array (não `.data.data`) — bug #45
    return (response?.data ?? []) as { id: string; name: string; email: string; type: string; store: string; active: boolean; }[]
  }

  /**
   * Lista os utilizadores ativos do sistema (escopo por papel) para popular
   * o filtro multi-select "Responsável" da listagem de requisições.
   *
   * Endpoint: GET /api/maintenance/users/active
   * - admin / cfo: todos os utilizadores ativos
   * - gerente / supervisor: utilizadores que partilham pelo menos uma loja
   * - staff: apenas o próprio utilizador
   *
   * Utilizadores inativos são sempre excluídos.
   */
  async getActiveUsers(): Promise<{
    id: string
    name: string
    email: string
    type: string
    store: string
    active: boolean
  }[]> {
    const response = await this.request<{
      success: boolean
      data: {
        id: string
        name: string
        email: string
        type: string
        store: string
        active: boolean
      }[]
      count: number
    }>({
      method: 'GET',
      url: '/maintenance/users/active'
    })

    return (response?.data ?? []) as { id: string; name: string; email: string; type: string; store: string; active: boolean; }[]
  }

  /**
   * Devolve a lista de lojas distintas com a contagem de ativos,
   * para alimentar os chips de filtro rápido por localização.
   * Substitui o `LOCATION_OPTIONS` hardcoded da PR #215.
   *
   * @returns Array de `{ value, assetCount }` ordenado por `assetCount` desc.
   */
  async getStoresWithUsage(): Promise<Array<{ value: string; assetCount: number }>> {
    // Backend `GET /api/stores/with-usage` devolve `{ success: true, data: [...] }`
    // (envelope `ApiResponse<T>` padrão do projeto — `T` vai dentro de `data`).
    // Esta rota foi corrigida para alinhar com este contrato; ver teste
    // integração no `tests/services/api.test.ts` (bloco `getStoresWithUsage
    // (envelope stores)`).
    const response = await this.request<Array<{ value: string; assetCount: number }>>({
      method: 'GET',
      url: '/stores/with-usage'
    })

    return (Array.isArray(response?.data) ? response.data : []) as Array<{
      value: string
      assetCount: number
    }>
  }

  /**
   * File upload methods
   */
  async uploadFile(file: File, metadata: {
    maintenanceId: string
    fileDescription?: string
  }): Promise<{
    id: string
    originalName: string
    webViewLink: string
    webContentLink: string
    size: number
    mimeType: string
  }> {
    const formData = new FormData()
    formData.append('file', file)
    formData.append('maintenanceId', metadata.maintenanceId)
    if (metadata.fileDescription) {
      formData.append('fileDescription', metadata.fileDescription)
    }

    const response = await this.request<{
      id: string
      originalName: string
      webViewLink: string
      webContentLink: string
      size: number
      mimeType: string
    }>({
      method: 'POST',
      url: '/files/upload',
      data: formData,
      headers: {
        'Content-Type': 'multipart/form-data',
      }
    })

    return response.data!
  }

  async uploadFiles(formData: FormData, onProgress?: (progress: number) => void): Promise<{ data: { files: { id?: string; originalName?: string; name?: string; webViewLink?: string }[] } }> {
    const response = await this.client.post('/files/upload', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          onProgress(progress)
        }
      }
    })
    return response.data
  }

  async getMaintenanceFiles(maintenanceId: string): Promise<{ id?: string; originalName?: string; name?: string; webViewLink?: string }[]> {
    // Backend retorna diretamente o array de arquivos
    const response = await this.client.get<{ id?: string; originalName?: string; name?: string; webViewLink?: string }[]>(`/files/maintenance/${maintenanceId}`)
    return response.data
  }

  async deleteFile(fileId: string): Promise<void> {
    await this.request({
      method: 'DELETE',
      url: `/files/${fileId}`
    })
  }

  /**
   * User management methods (admin only)
   */
  async getUsers(params?: {
    store?: string
    department?: string
    type?: string
    active?: boolean
    page?: number
    limit?: number
  }): Promise<{ items: User[]; pagination: PaginationInfo }> {
    const response = await this.request<{ items: User[]; pagination: PaginationInfo }>({
      method: 'GET',
      url: '/users',
      params
    })

    return response.data!
  }

  async createUser(data: {
    name: string
    email: string
    type: string
    store: string
    department: string
    active?: boolean
  }): Promise<User> {
    const response = await this.request<User>({
      method: 'POST',
      url: '/users',
      data
    })

    return response.data!
  }

  async updateUser(id: string, data: Partial<User>): Promise<User> {
    const response = await this.request<User>({
      method: 'PUT',
      url: `/users/${id}`,
      data
    })

    return response.data!
  }

  /**
   * Utility methods
   */
  async healthCheck(): Promise<{ status: string; timestamp: string }> {
    const response = await this.request<{ status: string; timestamp: string }>({
      method: 'GET',
      url: '/health',
      skipAuth: true
    })

    return response.data!
  }

  /**
   * Partner methods
   */
  async getPartners(params?: {
    status?: string
    region?: string
    specialization?: string
    includeInactive?: boolean
  }): Promise<Partner[]> {
    const response = await this.request<Partner[]>({
      method: 'GET',
      url: '/partners',
      params
    })

    return response.data || []
  }

  async getPartnerById(id: string): Promise<Partner | null> {
    try {
      const response = await this.request<Partner>({
        method: 'GET',
        url: `/partners/${id}`
      })

      return response.data || null
    } catch (error) {
      console.error(`Failed to get partner ${id}:`, error)
      return null
    }
  }

  async getPartnersByRegion(region: string, includeInactive = false): Promise<Partner[]> {
    const response = await this.request<Partner[]>({
      method: 'GET',
      url: `/partners/region/${region}`,
      params: { includeInactive }
    })

    return response.data || []
  }

  async getPartnersByStatus(status: string, includeInactive = false): Promise<Partner[]> {
    const response = await this.request<Partner[]>({
      method: 'GET',
      url: `/partners/status/${status}`,
      params: { includeInactive }
    })

    return response.data || []
  }

  async getPartnerForAsset(assetId: string): Promise<Partner | null> {
    try {
      const response = await this.request<{asset: Asset, partner: Partner}>({
        method: 'GET',
        url: `/partners/asset/${assetId}`
      })

      return response.data?.partner || null
    } catch (error) {
      console.error(`Failed to get partner for asset ${assetId}:`, error)
      return null
    }
  }

  /**
   * Recurrence methods (feature 005-add-recurring-maintenance)
   */

  /**
   * Obtém o perfil de recorrência de um ativo
   * Endpoint: GET /api/maintenance/recurrence/assets/{equipmentId}
   */
  async getAssetRecurrenceProfile(equipmentId: string): Promise<AssetRecurrenceProfileDTO | null> {
    try {
      const response = await this.request<AssetRecurrenceProfileDTO>({
        method: 'GET',
        url: `/maintenance/recurrence/assets/${equipmentId}`
      })

      return response.data || null
    } catch (error) {
      // Se for 404, retorna null (ativo sem recorrência configurada)
      if ((error as { statusCode?: number })?.statusCode === 404) {
        return null
      }
      throw error
    }
  }

  /**
   * Atualiza o perfil de recorrência de um ativo
   * Endpoint: PUT /api/maintenance/recurrence/assets/{equipmentId}
   */
  async updateAssetRecurrenceProfile(
    equipmentId: string,
    payload: {
      quantity: number
      unit: 'd' | 's' | 'm'
      inactive?: boolean
      lastExecutionDate?: string
    }
  ): Promise<AssetRecurrenceProfileDTO> {
    const response = await this.request<AssetRecurrenceProfileDTO>({
      method: 'PUT',
      url: `/maintenance/recurrence/assets/${equipmentId}`,
      data: payload
    })

    return response.data!
  }

  /**
   * Consulta próxima data sugerida para criação manual
   * Endpoint: GET /api/maintenance/recurrence/assets/{equipmentId}/next-occurrence
   */
  async getNextRecurrenceOccurrence(
    equipmentId: string,
    requestedDate?: string
  ): Promise<{
    equipmentId: string
    nextDueDate: string
    lastExecutionDate?: string
    recurrence: AssetRecurrenceProfileDTO
    conflict?: boolean
    conflictRequestId?: string
  } | null> {
    try {
      const params = requestedDate ? { requestedDate } : undefined
      // A rota next-occurrence responde RAW (sem wrapper {success,data}) com
      // { equipmentId, nextDueDate, parsedRecurrence: { quantity, unit } }. O
      // acesso `response.data` assumia wrapper e devolvia undefined → null, e o
      // backend usa `parsedRecurrence` (não `recurrence`), pelo que o MaintenanceForm
      // lia `response.recurrence` = undefined e o painel nunca aparecia (bug #54).
      const response = await this.request<{
        equipmentId: string
        nextDueDate: string | null
        parsedRecurrence?: { quantity: number; unit: RecurrenceUnit }
        error?: string
      }>({
        method: 'GET',
        url: `/maintenance/recurrence/assets/${equipmentId}/next-occurrence`,
        params
      })

      // request<T>() devolve o body já desembrulhado do axios, tipado como
      // ApiResponse<T> = { success, data: T }. A rota agora embrulha em
      // { success, data }, por isso `response.data` É o payload
      // { equipmentId, nextDueDate, parsedRecurrence }. O backend usa
      // `parsedRecurrence` (não `recurrence`), pelo que normalizamos aqui para o
      // DTO que o MaintenanceForm espera (AssetRecurrenceProfileDTO). Sem isto, o
      // painel de Manutenção Recorrente nunca aparecia (bug #54).
      const body = response.data
      if (!body?.parsedRecurrence || !body.nextDueDate) {
        return null
      }
      const { quantity, unit } = body.parsedRecurrence
      const now = new Date().toISOString()
      return {
        equipmentId: body.equipmentId,
        nextDueDate: body.nextDueDate,
        recurrence: {
          equipmentId: body.equipmentId,
          quantity,
          unit,
          recurrenceString: `${quantity}${unit}`,
          nextDueDate: body.nextDueDate,
          inactive: false,
          createdAt: now,
          updatedAt: now,
        },
      }
    } catch (error) {
      // Se for 204, retorna null (ativo sem recorrência configurada)
      if ((error as { statusCode?: number })?.statusCode === 204) {
        return null
      }
      throw error
    }
  }

  /**
   * Lista tentativas de geração automática com paginação
   * Endpoint: GET /api/maintenance/recurrence/generation-attempts
   */
  async getRecurringGenerationAttempts(params?: {
    status?: 'SUCCESS' | 'IGNORED' | 'ERROR'
    equipmentId?: string
    from?: string
    to?: string
    page?: number
    pageSize?: number
  }): Promise<{
    items: RecurringGenerationAttemptDTO[]
    pagination: {
      page: number
      pageSize: number
      total: number
    }
  }> {
    const response = await this.request<{
      items: RecurringGenerationAttemptDTO[]
      pagination: {
        page: number
        pageSize: number
        total: number
      }
    }>({
      method: 'GET',
      url: '/maintenance/recurrence/generation-attempts',
      params
    })

    return response.data!
  }

  /**
   * Cancel all pending requests
   */
  cancelAllRequests(): void {
    // Implementation for canceling pending requests if needed
    console.log('All pending requests cancelled')
  }

  /**
   * Reseta todos os caches relacionados a planilhas Google Sheets
   * Endpoint: POST /api/cache/full-reload
   */
  async resetSheetCaches(): Promise<{
    success: boolean
    message: string
    data?: {
      sheetsCacheCleared: boolean
      dataCacheManagerInvalidated: boolean
      durationMs: number
      timestamp: string
      clearedItems: {
        sheetsCacheEntries: number
        pendingPromises: number
        assetApprovalProfiles: number
        assets: number
      }
    }
  }> {
    try {
      const response = await this.client.request<{
        success: boolean
        message: string
        data: {
          sheetsCacheCleared: boolean
          dataCacheManagerInvalidated: boolean
          durationMs: number
          timestamp: string
          clearedItems: {
            sheetsCacheEntries: number
            pendingPromises: number
            assetApprovalProfiles: number
            assets: number
          }
        }
      }>({
        method: 'POST',
        url: '/cache/full-reload'
      })

      return response.data
    } catch (error) {
      console.error('Failed to reset sheet caches:', error)
      const errObj = error as Record<string, unknown>
      const responseData = errObj?.response as Record<string, unknown> | undefined
      const errMsg: string = (typeof (responseData?.data as Record<string, unknown>)?.message === 'string' ? (responseData?.data as Record<string, unknown>).message as string : null) || (typeof errObj?.message === 'string' ? errObj.message as string : null) || 'Falha ao resetar caches de planilha'
      throw new Error(errMsg)
    }
  }
}

// Export the class for testing purposes
export { ApiService }

// Export singleton instance
export const apiService = ApiService.getInstance()

// Export individual methods for convenience
export const {
  login,
  logout,
  searchAssets,
  getAsset,
  getMaintenanceRequests,
  getMaintenanceRequest,
  createMaintenanceRequest,
  updateMaintenanceRequest,
  updateMaintenanceStatus,
  approveMaintenanceRequest,
  approveMaintenanceRequestLegacy,
  rejectMaintenanceRequest,
  completeMaintenanceRequest,
  assignMaintenanceResponsibility,
  postMaintenanceUpdate,
  postMaintenanceUpdateLegacy,
  remindMaintenanceRequest,
  getInventoryStalledReport,
  uploadFile,
  uploadFiles,
  getMaintenanceFiles,
  deleteFile,
  getUsers,
  createUser,
  updateUser,
  getActiveUsers,
  getStoresWithUsage,
  healthCheck,
  cancelAllRequests,
  // Recurrence methods (feature 005-add-recurring-maintenance)
  getAssetRecurrenceProfile,
  updateAssetRecurrenceProfile,
  getNextRecurrenceOccurrence,
  getRecurringGenerationAttempts
} = apiService

// Export resetSheetCaches separately with proper binding
export const resetSheetCaches = apiService.resetSheetCaches.bind(apiService)

export default apiService